import { useEffect, useRef } from 'react';
import type { ArticleCard as ArticleCardPayload } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { CardGridSkeleton } from '@/routes/page-parts';
import { EmptyState, ErrorState, LoadMore } from '@/components/ui';
import { ArticleCard } from './ArticleCard';

/**
 * The grid every listing page renders — category, district, tag, author.
 *
 * Infinite scroll **with a working button**, which is the Phase 10 task list's wording
 * and the right shape: an `IntersectionObserver` loads the next page when the sentinel
 * comes into view, and the same action stays available as a real `<button>` for anyone
 * navigating by keyboard, using a screen reader, or on a browser where the observer
 * never fires. Infinite scroll that is *only* scroll is a listing some readers cannot
 * page through at all.
 */
export function ArticleFeed({
  articles,
  isPending,
  isError,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onRetry,
  emptyTitle,
  emptyBody,
}: {
  articles: ArticleCardPayload[];
  isPending: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  emptyTitle?: string;
  emptyBody?: string;
}) {
  const { t } = useLocale();
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage || isFetchingNextPage) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      // Start fetching a screenful early, so the next cards are usually there by the
      // time the reader reaches them.
      { rootMargin: '600px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore]);

  if (isError && articles.length === 0) {
    return (
      <ErrorState
        title={t('error.genericTitle')}
        body={t('error.genericBody')}
        retryLabel={t('action.retry')}
        onRetry={onRetry}
      />
    );
  }

  if (isPending && articles.length === 0) {
    return <CardGridSkeleton count={8} label={t('status.loading')} />;
  }

  if (articles.length === 0) {
    return <EmptyState title={emptyTitle ?? t('empty.noStories')} body={emptyBody} />;
  }

  return (
    <>
      {/*
        The grid's own heading, for the heading outline rather than for the eye. A
        listing page is `h1` (its title) then cards (`h3`), and a screen reader browsing
        by heading hears a level skipped — on the homepage the section titles fill that
        gap, but a listing has none to give.
      */}
      <h2 className="sr-only">{t('section.latest')}</h2>
      <div className="grid grid-cols-1 gap-4.5 sm:grid-cols-2 lg:grid-cols-4">
        {articles.map((article) => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </div>

      {isFetchingNextPage && (
        <div className="mt-4.5">
          <CardGridSkeleton count={4} label={t('status.loading')} />
        </div>
      )}

      <div ref={sentinel} aria-hidden="true" className="h-px" />

      <LoadMore
        className="mt-8"
        hasMore={hasNextPage}
        loading={isFetchingNextPage}
        onLoad={onLoadMore}
        label={t('action.loadMore')}
      />
    </>
  );
}
